import { createEmitter, ldb } from '@codesuma/baseline'
import type { RealtimeChannel } from '@supabase/supabase-js'
import db from './db'
import utils from '../utils'
import supabase, { supabase as sbClient } from './supabase'
import listsService from './lists'
import itemOrder from './item-order'
import push from './push'

export interface Item {
    id: string
    list_id: string
    name: string
    checked: boolean
    category: string
    // 'heading' = a section divider row; an item belongs to the heading above it
    // in display order. Absent (pre-009 rows) means a plain item.
    kind?: 'item' | 'heading'
    // The temp id this row was created from (migration 014). Set by the server
    // on rows it created for us; absent on older rows and on local items, whose
    // own `id` *is* the client id until they sync.
    client_id?: string | null
    user_id: string
    synced: boolean
    created_at: string
    updated_at: string
}

export interface ListMember {
    id: string
    list_id: string
    user_id: string | null
    email: string | null
    role: string
    status: string
    handle: string | null
    display_name: string | null
    created_at: string
}

const emitter = createEmitter()

// Items load a page at a time, newest first; scrolling to the bottom of the
// list asks for the next page (loadMore). The server caps a request at this
// many rows, so it is also the "was that page full?" test.
const PAGE_SIZE = 200

let items: Item[] = []
let currentListId: string | null = null
let channel: RealtimeChannel | null = null
// Paging state for the open list: the created_at of the last row fetched (the
// cursor for the page below it) and whether the last page came back full.
let oldestFetched: string | null = null
let hasMore = false

// Start the open list's paging over. The cached items stay — a fetch only ever
// replaces the window it asked for — but the cursor belongs to this session's
// fetches, so it can't carry over to a different list.
const resetPaging = (): void => {
    oldestFetched = null
    hasMore = false
}

const generateTempId = (): string => {
    return 'temp_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9)
}

// Ids of synced items deleted while offline. Hard delete is fine for a shopping
// list, but the server delete can fail offline; we queue the id and replay it on
// the next refresh so the item doesn't resurrect from the server.
const PENDING_DELETES = 'papa-pending-deletes'
const getPendingDeletes = (): string[] => ldb.get(PENDING_DELETES) || []
const queuePendingDelete = (id: string) => {
    const ids = getPendingDeletes()
    if (!ids.includes(id)) ldb.set(PENDING_DELETES, [...ids, id])
}
const unqueuePendingDelete = (id: string) => {
    ldb.set(PENDING_DELETES, getPendingDeletes().filter(i => i !== id))
}

// Replay queued deletes; on success drop them from the queue. Stop on the first
// failure (still offline) so the rest stay queued for next time.
const syncPendingDeletes = async (): Promise<void> => {
    const ids = getPendingDeletes()
    if (!ids.length) return
    await supabase.auth.ensureSession()
    for (const id of ids) {
        try {
            await utils.http.remove(`/api/v1/lists/${id}`, {})
            unqueuePendingDelete(id)
        } catch (err) {
            // 404 = already gone on the server (deleted from another device).
            // That's the outcome we wanted — drop it, or it clogs the queue
            // and blocks every delete behind it forever.
            if (String(err).includes('HTTP 404')) {
                unqueuePendingDelete(id)
                continue
            }
            console.warn('Failed to replay pending delete:', id, err)
            break
        }
    }
}

// Ticks and renames of synced items that the server hasn't confirmed yet, by
// item id. A fetch takes the server's copy of a synced item as the truth, so an
// edit whose PUT failed (offline, or sent before the session was restored) was
// silently undone by the next refresh. Now an edit is queued *before* its PUT
// and stays queued until the server has it: fetches replay the queue, and what
// is still pending is laid over the server's rows (fetchPage, Realtime UPDATE).
type Edit = { checked?: boolean; name?: string }
const PENDING_EDITS = 'papa-pending-edits'
const getPendingEdits = (): Record<string, Edit> => ldb.get(PENDING_EDITS) || {}
const queuePendingEdit = (id: string, edit: Edit) => {
    const edits = getPendingEdits()
    ldb.set(PENDING_EDITS, { ...edits, [id]: { ...edits[id], ...edit } })
}
// The server now holds `sent`. Clear only the fields that still hold what was
// sent: a tick whose success arrives late must not clear the untick queued
// after it, and a tick must not clear a rename still waiting beside it.
const unqueuePendingEdit = (id: string, sent: Edit) => {
    const edits = getPendingEdits()
    const left: Edit = { ...edits[id] }
    for (const key of Object.keys(sent) as (keyof Edit)[]) {
        if (left[key] === sent[key]) delete left[key]
    }
    if (Object.keys(left).length) edits[id] = left
    else delete edits[id]
    ldb.set(PENDING_EDITS, edits)
}

// PUT one edit. False means it stays queued: offline, no session yet (401) or a
// server error. A 400/403/404 won't change on retry (invalid edit, access
// revoked, item gone), so it's dropped rather than left to block the queue.
const sendEdit = async (id: string, edit: Edit): Promise<boolean> => {
    try {
        await utils.http.put(`/api/v1/lists/${id}`, edit)
    } catch (err) {
        console.warn('Failed to sync edit to server:', id, err)
        if (!/HTTP 40[034]/.test(String(err))) return false
    }
    unqueuePendingEdit(id, edit)
    return true
}

// Replay queued edits. Stop at the first one that has to wait (still offline)
// so the rest stay queued for next time. The last replay wins: a member's
// change made while ours sat in the queue is overwritten, as with deletes.
const syncPendingEdits = async (): Promise<void> => {
    if (!Object.keys(getPendingEdits()).length) return
    await supabase.auth.ensureSession()
    for (const id of Object.keys(getPendingEdits())) {
        // Re-read: an edit made while the replay runs replaces the older value.
        const edit = getPendingEdits()[id]
        if (edit && !(await sendEdit(id, edit))) break
    }
}

const sortItems = (list: Item[]): Item[] => {
    return list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
}

// Emit a change and keep the parent list's circle counts (total + checked) in
// step, so the home page shows fresh "done/total" progress when you return to it.
// Section headings are structure, not tasks — they don't count.
const emitChange = (): void => {
    emitter.emit('change', items)
    if (currentListId) {
        const tasks = items.filter(i => i.kind !== 'heading')
        listsService.updateCounts(currentListId, tasks.length, tasks.filter(i => i.checked).length)
    }
}

// Make listId the active list right away (synchronous). The list page calls
// this on enter, before the deferred init(), so an item added in that window is
// stamped with the right list_id and its change event isn't dropped.
const setActive = (listId: string): void => {
    if (currentListId === listId) return
    currentListId = listId
    items = []
    resetPaging()
}

// Load the given list: instant view from IndexedDB, then sync from server
const init = async (listId: string): Promise<void> => {
    currentListId = listId
    resetPaging()

    // Load from IDB first for instant offline view (only this list's items)
    const stored = await db.all<Item>(db.STORE)
    if (currentListId !== listId) return // another list was opened while we read
    const mine = stored.filter(i => i.list_id === listId)
    // Keep anything added while the IDB read was in flight — its save may have
    // committed after the read, and dropping it here would blank the row the
    // user just typed.
    for (const it of items) if (!mine.some(m => m.id === it.id)) mine.push(it)
    items = sortItems(mine)
    emitChange()

    try {
        // A restored session's token must be ready before the server fetch —
        // but offline that wait can take ~30s, so it must not gate the local
        // paint above.
        await supabase.auth.waitForInitialization()
        if (currentListId !== listId) return
        await fetchFromServer()
        if (currentListId !== listId) return // the new list's own init subscribes
        subscribeRealtime()
    } catch (err) {
        console.warn('Failed to load list from server (offline?):', err)
    }
}

// Fetch one page of items from the server, update IndexedDB, refresh memory.
// Without `before` this is the newest page; with it, the page below that
// timestamp (see loadMore).
//
// A page only speaks for its own created_at window. Anything older simply
// wasn't asked for, so it is left alone — reading a partial view as "all the
// server has" is what used to wipe a long list's older items out of the local
// cache on every refresh.
const fetchPage = async (before?: string): Promise<void> => {
    // This whole response belongs to the list that was open when we asked. Open
    // another list mid-flight and it must be abandoned — otherwise it lands in
    // the new list's page and wipes the new list's IndexedDB cache on the way.
    const listId = currentListId
    try {
        // Replay offline deletes first so the server stops returning them.
        await syncPendingDeletes()
        // Same for ticks and renames, so the page below already has them.
        await syncPendingEdits()

        const query = [`limit=${PAGE_SIZE}`]
        if (listId) query.push(`list_id=${listId}`)
        if (before) query.push(`before=${encodeURIComponent(before)}`)
        const res = await utils.http.get<{ items: Item[]; order?: string[] }>(`/api/v1/lists?${query.join('&')}`)
        if (currentListId !== listId) return
        // The shared drag order travels with the first page — adopt it before
        // the emit below so the first paint is already in everyone else's
        // sequence. The pages under it are the same list's tail, so they don't
        // carry it.
        if (listId && !before) itemOrder.adopt(listId, res.order)

        const page = res.items || []
        // A short page means there is nothing below it, so this fetch speaks
        // for everything older as well.
        const full = page.length === PAGE_SIZE
        const floor = full ? page[page.length - 1].created_at : null

        // Guard against resurrection: drop anything still queued for deletion
        // (replay above may have failed if we're offline again).
        const pendingDeletes = new Set(getPendingDeletes())
        // And against reversion: an edit still queued wins over the server's copy.
        const pendingEdits = getPendingEdits()
        const serverItems = page
            .filter(item => !pendingDeletes.has(item.id))
            .map(item => ({ ...item, ...pendingEdits[item.id], synced: true }))

        const returned = new Set(page.map(i => i.id))
        // A lost create response leaves an item marked unsynced here while its
        // row exists there under our temp id as client_id (migration 014);
        // keeping both would show it twice.
        const landed = new Set(page.map(i => i.client_id).filter(Boolean) as string[])
        // The slice of time this page is authoritative about: under `before`,
        // which is where we asked it to start, and down to its last row.
        const inWindow = (item: Item) =>
            (!before || item.created_at < before) && (!floor || item.created_at >= floor)
        // What this page replaces: rows it returned, unsynced local rows the
        // server turns out to hold already, and synced rows inside its window
        // that it did not return (deleted from another device). Older rows and
        // still-pending local ones are none of its business.
        const stale = (item: Item) =>
            returned.has(item.id) || (item.synced ? inWindow(item) : landed.has(item.id))

        // Touch only THIS list's cached items, preserving other lists' local
        // data (e.g. starter lists not opened yet).
        const cachedItems = await db.all<Item>(db.STORE)
        for (const it of cachedItems) {
            if (it.list_id === listId && stale(it)) await db.delete(db.STORE, it.id)
        }
        for (const item of serverItems) {
            await db.save(db.STORE, item)
        }

        if (currentListId !== listId) return // switched lists during the writes
        // Past the last guard, so this is the open list's paging state, not a
        // list the user has already left. The cursor only ever descends: a
        // re-subscribe refetches the newest page (see subscribeRealtime), and
        // rewinding to its floor would walk the user back down through pages
        // they already have.
        hasMore = full
        if (floor && (!oldestFetched || floor < oldestFetched)) oldestFetched = floor
        items = sortItems([...serverItems, ...items.filter(it => !stale(it))])
        emitChange()
    } catch (err) {
        console.warn('Failed to fetch from server (offline?):', err)
    }
}

// Refresh the newest page. Every existing caller means this: paging only ever
// adds older rows underneath.
const fetchFromServer = (): Promise<void> => fetchPage()

// The page of older items below the ones we have. The list page calls this
// when the user scrolls near the bottom, so it must be cheap to call over and
// over: it does nothing while a page is in flight, once the server has nothing
// older, or within a second of a failed attempt — offline, the scroll would
// otherwise spin on a dead request.
let loadingMore = false
let lastLoadMore = 0
const loadMore = async (): Promise<void> => {
    if (loadingMore || !hasMore || !oldestFetched) return
    if (Date.now() - lastLoadMore < 1000) return
    lastLoadMore = Date.now()
    loadingMore = true
    try {
        await fetchPage(oldestFetched)
    } finally {
        loadingMore = false
    }
}

// Is there another page under what's loaded? Drives the list page's footer,
// which is both the "more is coming" label and the scroll trigger.
const hasMoreItems = (): boolean => hasMore

const getAll = (): Item[] => {
    return [...items]
}

const getActive = (): Item[] => {
    return items.filter(i => !i.checked)
}

const getCompleted = (): Item[] => {
    return items.filter(i => i.checked)
}

// On the first edit of a not-yet-synced list (e.g. a starter list), push the
// list itself, then replay its unsynced items. No-op once everything is synced.
//
// Calls are serialized on flushChain: add()/remove()/toggle()/update() all fire
// this without awaiting it from the UI, so two edits made in quick succession
// (e.g. add an item, then delete another before the first sync round-trips)
// would otherwise run overlapping syncPending() loops. Each loop snapshots
// "still unsynced" items independently, and an item only flips to synced after
// its POST resolves — so both loops could see the same item, POST it twice, and
// create a duplicate row on the server. Running flushes one at a time means the
// second loop's snapshot is taken after the first has finished, so it never
// re-sees an item the first one already posted.
let flushChain: Promise<void> = Promise.resolve()
const flush = async (): Promise<void> => {
    const listId = currentListId
    if (!listId) return
    flushChain = flushChain
        .then(async () => {
            if (currentListId !== listId) return
            await listsService.ensureSynced(listId)
            if (currentListId !== listId) return
            await syncPending()
        })
        .catch(err => console.warn('flush failed:', err))
    await flushChain
}

// Replay entry point for callers outside this module (the `online` handler).
// It queues on flushChain for exactly the reason above: a reconnect firing
// while an add() is mid-flight would otherwise run a second syncPending() loop
// over the same still-unsynced items and post them a second time. Coming back
// online is precisely when that overlap happens, so this was the open side
// door in the guard.
const replayPending = async (): Promise<void> => {
    flushChain = flushChain
        .then(syncPending)
        .catch(err => console.warn('replay failed:', err))
    await flushChain
}

const add = async (name: string, category = 'General', kind: 'item' | 'heading' = 'item'): Promise<Item> => {
    const now = new Date().toISOString()
    const tempId = generateTempId()

    const item: Item = {
        id: tempId,
        list_id: currentListId || '',
        name,
        checked: false,
        category,
        kind,
        user_id: '',
        synced: false,
        created_at: now,
        updated_at: now,
    }

    // Save to IDB immediately
    await db.save(db.STORE, item)
    items.unshift(item)
    emitChange()

    // Push the list (if it's a not-yet-synced starter/offline list) + this item
    // and any other unsynced items.
    await flush()

    return item
}

const toggle = async (id: string): Promise<Item | undefined> => {
    const item = items.find(i => i.id === id)
    if (!item) return undefined

    item.checked = !item.checked
    item.updated_at = new Date().toISOString()

    // Update IDB
    await db.save(db.STORE, { ...item })
    emitChange()

    // Unsynced (e.g. starter) item → push the list + items; else sync the change.
    if (id.startsWith('temp_')) {
        await flush()
    } else {
        const edit = { checked: item.checked }
        queuePendingEdit(id, edit)
        await sendEdit(id, edit)
    }

    return item
}

const update = async (id: string, name: string): Promise<Item | undefined> => {
    const item = items.find(i => i.id === id)
    if (!item) return undefined

    item.name = name
    item.updated_at = new Date().toISOString()

    // Update IDB
    await db.save(db.STORE, { ...item })
    emitChange()

    // Unsynced (e.g. starter) item → push the list + items; else sync the change.
    if (id.startsWith('temp_')) {
        await flush()
    } else {
        queuePendingEdit(id, { name })
        await sendEdit(id, { name })
    }

    return item
}

const remove = async (id: string): Promise<void> => {
    // Remove from IDB
    await db.delete(db.STORE, id)
    items = items.filter(i => i.id !== id)
    emitChange()

    // Removing a still-unsynced (e.g. starter) item → push the list + remaining
    // items; else delete it on the server.
    if (id.startsWith('temp_')) {
        await flush()
    } else {
        try {
            await utils.http.remove(`/api/v1/lists/${id}`, {})
        } catch (err) {
            // 404 = already gone on the server — mission accomplished, move on.
            if (String(err).includes('HTTP 404')) return
            // Likely offline — queue it so the next refresh replays the delete
            // and the item doesn't resurrect from the server in the meantime.
            console.warn('Failed to sync delete to server, queuing:', err)
            queuePendingDelete(id)
        }
    }
}

// Sync any unsynced items to server in one request (call when coming back
// online). `items` is already newest-first (sortItems), so the snapshot below
// is already top-to-bottom — the server stamps each row's created_at by its
// position in that array (see api/services/lists/create-bulk.ts) rather than
// relying on N separate request times, which is what made a single-item-per-
// request loop preserve on-screen order before. Sections still depend on
// order, since an item belongs to the heading above it.
const syncPending = async (): Promise<void> => {
    // Same rule as fetchFromServer: these items belong to the list that was
    // open when we started, so don't apply the result to a different list's
    // in-memory state if the user has since moved on — but still finish
    // writing the synced rows to IndexedDB, since they're genuinely synced
    // for the list they belong to.
    const listId = currentListId
    const unsynced = items.filter(i => !i.synced)
    if (!unsynced.length) return
    await supabase.auth.ensureSession()
    try {
        const res = await utils.http.post<{ items: Item[] }>('/api/v1/lists/bulk', {
            list_id: listId,
            items: unsynced.map(item => ({
                name: item.name,
                category: item.category,
                checked: item.checked,
                kind: item.kind || 'item',
                // Makes the create idempotent: replaying this batch after a
                // lost response returns the rows already inserted under this
                // id instead of inserting a second copy (migration 014).
                client_id: item.id,
            })),
        })
        const stillCurrent = currentListId === listId
        for (let i = 0; i < unsynced.length; i++) {
            const local = unsynced[i]
            const synced: Item = { ...res.items[i], synced: true }

            // Deleted locally while this request was in flight — undo the
            // create instead of leaving an orphaned row that resurrects on the
            // next fetch (and jumps to the top, being the newest thing there).
            if (stillCurrent && !items.some(i2 => i2.id === local.id)) {
                utils.http.remove(`/api/v1/lists/${synced.id}`, {}).catch(() => { /* best effort */ })
                continue
            }

            await db.delete(db.STORE, local.id)
            await db.save(db.STORE, synced)
            if (stillCurrent) {
                const idx = items.findIndex(i2 => i2.id === local.id)
                if (idx !== -1) items[idx] = synced
            }
        }
        if (stillCurrent) emitChange()
    } catch (err) {
        console.warn('Failed to sync pending items:', err)
    }
}

// Live updates via Supabase Realtime: Postgres itself is the broadcast bus,
// so members see each other's changes no matter which API machine handled
// the write. RLS (migration 002) scopes the subscription to lists the user
// owns or is a member of; supabase-js handles reconnection and heartbeats.
const subscribeRealtime = () => {
    if (channel) {
        sbClient.removeChannel(channel)
        channel = null
    }
    if (!currentListId) return
    // No session yet = nothing ever written to the server, so there's nothing
    // to hear about. A session appears on the first write (ensureSession) and
    // the next list open subscribes.
    const token = supabase.auth.getToken()
    if (!token) return
    sbClient.realtime.setAuth(token)

    const listId = currentListId
    let firstSubscribe = true
    channel = sbClient
        .channel(`list-items-${listId}`)
        .on('postgres_changes',
            { event: '*', schema: 'public', table: 'list_items', filter: `list_id=eq.${listId}` },
            handleRealtimeEvent)
        // The list's own row carries what the items table can't: the picked
        // color and the shared drag order. Migration 012 put `lists` in the
        // publication so a member's recolor or reorder arrives here live
        // instead of waiting for the next full refresh.
        .on('postgres_changes',
            { event: 'UPDATE', schema: 'public', table: 'lists', filter: `id=eq.${listId}` },
            handleListRowEvent)
        .subscribe((status) => {
            if (status === 'SUBSCRIBED' && !firstSubscribe) {
                // Re-subscribed after a gap — refetch to heal missed events.
                fetchFromServer()
            }
            if (status === 'SUBSCRIBED') firstSubscribe = false
        })
}

// Another member (or this user's other device) recolored or reordered the open
// list. Both live on the list row, so one handler covers them: mirror them into
// the caches every surface reads, then re-emit so the page repaints and the
// rows glide (the render's FLIP pass) into the new order.
const handleListRowEvent = (payload: any) => {
    const row = payload.new
    if (!row || row.id !== currentListId) return
    listsService.applyRemoteColor(row.id, typeof row.color === 'number' ? row.color : null)
    itemOrder.adopt(row.id, row.item_order)
    emitChange()
}

const handleRealtimeEvent = (payload: any) => {
    if (payload.eventType === 'INSERT') {
        const incoming: Item = { ...payload.new, synced: true }
        if (incoming.list_id !== currentListId) return
        // Skip echoes of our own optimistic adds: the row may already be here
        // by id (the POST response won the race) or still be sitting as the
        // temp_ copy (this event won — syncPending will swap it in), which the
        // row's client_id names exactly. This used to match on the *name*
        // instead, which silently swallowed another member's item whenever it
        // happened to match something we hadn't synced yet.
        if (items.some(i => i.id === incoming.id || (!!incoming.client_id && i.id === incoming.client_id))) return
        db.save(db.STORE, incoming)
        items.unshift(incoming)
        items = sortItems(items)
        emitChange()
    } else if (payload.eventType === 'UPDATE') {
        // An edit of ours still queued wins, as in fetchPage.
        const incoming: Item = { ...payload.new, ...getPendingEdits()[payload.new.id], synced: true }
        const idx = items.findIndex(i => i.id === incoming.id)
        if (idx !== -1) {
            items[idx] = incoming
            db.save(db.STORE, incoming)
            emitChange()
        }
    } else if (payload.eventType === 'DELETE') {
        // old carries the full row thanks to REPLICA IDENTITY FULL (migration 006)
        const deletedId = payload.old?.id
        if (deletedId && items.some(i => i.id === deletedId)) {
            items = items.filter(i => i.id !== deletedId)
            db.delete(db.STORE, deletedId)
            emitChange()
        }
    }
}

// Members management
const getMembers = async (): Promise<ListMember[]> => {
    if (!currentListId) return []
    try {
        const res = await utils.http.get<{ members: ListMember[] }>(`/api/v1/lists/${currentListId}/members`)
        return res.members || []
    } catch (err) {
        console.warn('Failed to get members:', err)
        return []
    }
}

export interface Contact {
    handle: string | null
    display_name: string | null
    email: string | null
}

// Everyone the user already shares a list with — the share panel offers these
// so a second list doesn't mean typing a sharing ID out again.
const getContacts = async (): Promise<Contact[]> => {
    if (!supabase.auth.isAuthenticated()) return [] // nothing shared yet
    try {
        const res = await utils.http.get<{ contacts: Contact[] }>('/api/v1/lists/contacts')
        return res.contacts || []
    } catch (err) {
        console.warn('Failed to load contacts:', err)
        return []
    }
}

const prepareShare = async (listId: string): Promise<void> => {
    // Sharing needs an auth session and the list on the server: mint/await the
    // session, then push a not-yet-synced (starter/offline) list before inviting.
    // A slow sign-in or sync can outlive the page. Never let that request share
    // whichever list happens to be open when it finishes.
    await supabase.auth.ensureSession()
    if (currentListId !== listId) throw new Error('The open list changed. Reopen the list to share it.')
    await flush()
    if (currentListId !== listId) throw new Error('The open list changed. Reopen the list to share it.')
}

const addMember = async (email: string): Promise<ListMember | null> => {
    const listId = currentListId
    if (!listId) return null
    await prepareShare(listId)
    try {
        return await utils.http.post<ListMember>(`/api/v1/lists/${listId}/members`, { email })
    } catch (err: any) {
        console.error('Failed to add member:', err)
        throw err
    }
}

// Share with the user behind a sharing handle (e.g. "moonlight456"). Creates a
// pending invite the recipient must accept.
const shareByHandle = async (handle: string): Promise<void> => {
    const listId = currentListId
    if (!listId) return
    // Same as addMember: session + server-side list must exist before sharing.
    await prepareShare(listId)
    await utils.http.post(`/api/v1/lists/${listId}/members/by-handle`, { handle })
    // You now share a list — a natural moment to ask for notification permission
    push.enable()
}

const removeMember = async (memberId: string): Promise<void> => {
    if (!currentListId) return
    try {
        await utils.http.remove(`/api/v1/lists/${currentListId}/members/${memberId}`, {})
    } catch (err) {
        console.error('Failed to remove member:', err)
        throw err
    }
}

const getListId = (): string | null => currentListId

const on = emitter.on.bind(emitter)

export default {
    setActive,
    init,
    fetchFromServer,
    loadMore,
    hasMoreItems,
    replayPending,
    getAll,
    getActive,
    getCompleted,
    add,
    toggle,
    update,
    remove,
    getMembers,
    getContacts,
    addMember,
    shareByHandle,
    removeMember,
    getListId,
    on,
}
